/*
 * Streaming markdown stability.
 *
 * While an answer streams, the whole text used to be re-parsed by
 * ReactMarkdown on every update, so completed content re-rendered constantly
 * and could visibly shift as later chunks arrived. This module finds the last
 * SAFE block boundary in the text so the render can split it into a stable
 * head, which only re-parses when a new block completes, and a live tail,
 * which is the only part that re-parses per frame.
 *
 * "Safe" means splitting there cannot change what the markdown means:
 *
 *   - never inside a fenced code block (a ``` fence spanning the boundary)
 *   - never between two list items: `- a\n\n- b` is ONE loose list, and
 *     splitting it renders two lists with different spacing
 *   - never between blockquote paragraphs, for the same reason
 *   - never just after a table row, so a table is never torn in half
 *
 * When no boundary is safe, everything is tail, which is exactly today's
 * behaviour. When the stream finishes the caller renders the full text in one
 * piece again, so the final render is byte-identical to the non-streaming
 * path. Known limitation: reference-style link definitions ([id]: url) only
 * resolve within their own side of the split while streaming; Gia's prose
 * never uses them.
 */

const LIST_OR_QUOTE = /^\s{0,3}(?:[-+*]\s|\d{1,9}[.)]\s|>)/;
const TABLE_ROW = /^\s{0,3}\|/;
const FENCE = /^\s{0,3}(```|~~~)/;

/**
 * Split streaming markdown into { stable, tail } at the last safe blank-line
 * boundary. stable keeps its trailing blank line so stable + tail === text.
 */
export function stableSplit(text) {
  if (!text) return { stable: '', tail: '' };

  // Fence state at every line start, computed once in a single pass.
  const lines = text.split('\n');
  const fenceOpenAt = new Array(lines.length + 1);
  let open = false;
  for (let i = 0; i < lines.length; i++) {
    fenceOpenAt[i] = open;
    if (FENCE.test(lines[i])) open = !open;
  }
  fenceOpenAt[lines.length] = open;

  // Walk blank-line boundaries from the end, looking for a safe one.
  for (let i = lines.length - 1; i > 0; i--) {
    if (lines[i - 1].trim() !== '' || fenceOpenAt[i]) continue;

    // The boundary is the blank line at i-1. Find the last non-blank line
    // before it and the first non-blank line after it.
    let before = i - 2;
    while (before >= 0 && lines[before].trim() === '') before--;
    let after = i;
    while (after < lines.length && lines[after].trim() === '') after++;

    if (before < 0) continue;              // nothing stable before it
    if (after >= lines.length) continue;   // the "tail" would be empty air

    const prev = lines[before];
    const next = lines[after];

    // A list/quote continuing across the blank line is one construct.
    if (LIST_OR_QUOTE.test(prev) && LIST_OR_QUOTE.test(next)) continue;
    /*
     * Never split just after a table row: the rows behind the boundary and
     * ahead of it may be one table. Splitting BEFORE a table's first row is
     * fine, since the whole table then lives in the tail.
     */
    if (TABLE_ROW.test(prev)) continue;

    const stable = lines.slice(0, i).join('\n') + '\n';
    return { stable, tail: text.slice(stable.length) };
  }

  return { stable: '', tail: text };
}
